import {
  AppBar, Toolbar, Box, Typography, Button, Stack,
  IconButton, Drawer, List, ListItem, ListItemText, Divider,
  useScrollTrigger, Popper, Paper, Grow, ClickAwayListener,
  Collapse,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowForward as ArrowIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Favorite as DonateIcon,
  Groups as TeamIcon,
  Info as AboutIcon,
  KeyboardArrowDown as ChevronDown,
  ExpandMore as ExpandMore,
  ExpandLess as ExpandLess,
} from "@mui/icons-material";
import { useState, useRef } from "react";

const SDG_COLORS = [
  "#E5243B","#DDA63A","#4C9F38","#C5192D","#FF3A21",
  "#26BDE2","#FCC30B","#A21942","#FD6925","#DD1367",
  "#FD9D24","#BF8B2E","#3F7E44","#0A97D9","#56C02B",
  "#00689D","#19486A",
];

const BROWN      = "#5C3311";
const BROWN_DARK = "#3e220b";
const GOLD       = "#DDA63A";

/* ── nav structure ── */
const navLinks = [
  { label: "Home",     path: "/" },
  {
    label: "About",
    path:  "/about",
    dropdown: [
      { label: "About BTSA", path: "/about",      icon: <AboutIcon sx={{ fontSize: 16 }} /> },
      { label: "Our Team",   path: "/about/team", icon: <TeamIcon  sx={{ fontSize: 16 }} /> },
    ],
  },
  { label: "Services", path: "/services" },
  { label: "Contact",  path: "/contact" },
];

/* ── desktop dropdown menu ── */
function DesktopDropdown({ items, anchorEl, open, onClose }) {
  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      transition
      style={{ zIndex: 1400 }}
    >
      {({ TransitionProps }) => (
        <Grow
          {...TransitionProps}
          style={{ transformOrigin: "top left" }}
          timeout={180}
        >
          <Paper
            elevation={0}
            sx={{
              mt: 1,
              minWidth: 200,
              borderRadius: "10px",
              border: "1px solid rgba(0,0,0,0.08)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              overflow: "hidden",
            }}
          >
            {/* SDG top strip */}
            <Box sx={{ display: "flex", height: "2px" }}>
              {SDG_COLORS.map((c, i) => <Box key={i} sx={{ flex: 1, bgcolor: c }} />)}
            </Box>

            <Box sx={{ py: 0.5 }}>
              {items.map((item) => (
                <Box
                  key={item.path}
                  component={Link}
                  to={item.path}
                  onClick={onClose}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.2,
                    textDecoration: "none",
                    color: "#333",
                    fontSize: "13.5px",
                    fontWeight: 500,
                    transition: "all 0.18s",
                    "&:hover": {
                      bgcolor: "rgba(221,166,58,0.08)",
                      color: BROWN,
                      pl: 2.5,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 28, height: 28,
                      borderRadius: "7px",
                      bgcolor: "rgba(92,51,17,0.06)",
                      color: BROWN,
                      flexShrink: 0,
                      transition: "background 0.18s",
                      ".MuiBox-root:hover &": { bgcolor: "rgba(221,166,58,0.15)" },
                    }}
                  >
                    {item.icon}
                  </Box>
                  {item.label}
                </Box>
              ))}
            </Box>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
}

export default function Navbar() {
  const trigger     = useScrollTrigger({ disableHysteresis: true, threshold: 60 });
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen]           = useState(false);
  const [aboutDropOpen, setAboutDropOpen]     = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const aboutAnchorRef = useRef(null);

  const bgColor   = trigger ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.92)";
  const mutedText = "#555555";

  const handleAboutEnter = () => setAboutDropOpen(true);
  const handleAboutLeave = () => setAboutDropOpen(false);
  const handleAboutClose = () => setAboutDropOpen(false);

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: bgColor,
          backdropFilter: "blur(18px)",
          boxShadow: trigger
            ? "0 2px 24px rgba(0,0,0,0.10)"
            : "0 1px 0 rgba(0,0,0,0.07)",
          borderBottom: "1px solid rgba(0,0,0,0.07)",
          transition: "all 0.35s ease",
          height: { xs: 58, md: trigger ? 62 : 74 },
        }}
      >
        {/* SDG colour strip */}
        <Box sx={{ display: "flex", height: "3px" }}>
          {SDG_COLORS.map((c, i) => <Box key={i} sx={{ flex: 1, bgcolor: c }} />)}
        </Box>

        <Toolbar
          sx={{
            height: "100%",
            px: { xs: 2, sm: 3, md: 5, lg: 8 },
            minHeight: "unset !important",
          }}
        >
          {/* ── Logo ── */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ flexGrow: 1 }}
            component={Link}
            to="/"
            style={{ textDecoration: "none" }}
          >
            <Box
              sx={{
                position: "relative",
                width: { xs: 36, md: trigger ? 38 : 46 },
                height: { xs: 36, md: trigger ? 38 : 46 },
                flexShrink: 0,
                transition: "all 0.3s",
              }}
            >
              <Box
                sx={{
                  position: "absolute", inset: -2, borderRadius: "50%",
                  background: `conic-gradient(${SDG_COLORS.map((c, i) =>
                    `${c} ${(i / SDG_COLORS.length) * 100}% ${((i + 1) / SDG_COLORS.length) * 100}%`
                  ).join(", ")})`,
                  "@keyframes spin": {
                    from: { transform: "rotate(0deg)" },
                    to:   { transform: "rotate(360deg)" },
                  },
                  animation: "spin 20s linear infinite",
                  opacity: 0.85,
                }}
              />
              <Box sx={{ position: "absolute", inset: 1.5, borderRadius: "50%", bgcolor: "#fff" }} />
              <Box
                component="img"
                src="/Picture1.png"
                alt="BTSA Logo"
                sx={{
                  position: "absolute", inset: 1.5,
                  width: "calc(100% - 3px)", height: "calc(100% - 3px)",
                  borderRadius: "50%", objectFit: "cover", zIndex: 2,
                }}
              />
            </Box>

            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography
                sx={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: { sm: "13px", md: trigger ? "13px" : "14.5px" },
                  fontWeight: 700,
                  color: "#1a1a1a",
                  lineHeight: 1.2,
                  transition: "font-size 0.3s",
                }}
              >
                Build To Shelter Africa
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: "9px",
                  color: GOLD,
                  letterSpacing: "2px",
                  textTransform: "uppercase",
                  mt: 0.2,
                }}
              >
                Pan-African NGO · Est. 2025
              </Typography>
            </Box>
          </Stack>

          {/* ── Desktop Nav Links ── */}
          <Stack
            direction="row"
            spacing={{ md: 3, lg: 4 }}
            sx={{ display: { xs: "none", md: "flex" }, mr: { md: 2, lg: 3 } }}
          >
            {navLinks.map((link) => {
              const isActive  = pathname === link.path || pathname.startsWith(link.path + "/");
              const hasDropdown = !!link.dropdown;

              if (hasDropdown) {
                return (
                  <ClickAwayListener key={link.path} onClickAway={handleAboutClose}>
                    <Box
                      ref={aboutAnchorRef}
                      onMouseEnter={handleAboutEnter}
                      onMouseLeave={handleAboutLeave}
                      sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                    >
                      <Typography
                        component={Link}
                        to={link.path}
                        sx={{
                          color: isActive ? GOLD : mutedText,
                          fontSize: { md: "13px", lg: "14px" },
                          fontWeight: isActive ? 700 : 500,
                          textDecoration: "none",
                          transition: "color 0.2s",
                          position: "relative",
                          whiteSpace: "nowrap",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.3,
                          "&:hover": { color: GOLD },
                          "&::after": isActive
                            ? {
                                content: '""', position: "absolute",
                                bottom: -2, left: 0, width: "calc(100% - 18px)", height: "2px",
                                background: "linear-gradient(90deg, #E5243B, #DDA63A, #4C9F38)",
                                borderRadius: "1px",
                              }
                            : {},
                          "&:hover::after": {
                            content: '""', position: "absolute",
                            bottom: -2, left: 0, width: "calc(100% - 18px)", height: "2px",
                            background: "linear-gradient(90deg, #E5243B, #DDA63A, #4C9F38)",
                            borderRadius: "1px",
                          },
                        }}
                      >
                        {link.label}
                        <ChevronDown
                          sx={{
                            fontSize: "16px",
                            color: isActive ? GOLD : mutedText,
                            transition: "transform 0.2s",
                            transform: aboutDropOpen ? "rotate(180deg)" : "rotate(0deg)",
                          }}
                        />
                      </Typography>

                      <DesktopDropdown
                        items={link.dropdown}
                        anchorEl={aboutAnchorRef.current}
                        open={aboutDropOpen}
                        onClose={handleAboutClose}
                      />
                    </Box>
                  </ClickAwayListener>
                );
              }

              return (
                <Typography
                  key={link.path}
                  component={Link}
                  to={link.path}
                  sx={{
                    color: isActive ? GOLD : mutedText,
                    fontSize: { md: "13px", lg: "14px" },
                    fontWeight: isActive ? 700 : 500,
                    textDecoration: "none",
                    cursor: "pointer",
                    transition: "color 0.2s",
                    position: "relative",
                    whiteSpace: "nowrap",
                    "&:hover": { color: GOLD },
                    "&::after": isActive
                      ? {
                          content: '""', position: "absolute",
                          bottom: -2, left: 0, width: "100%", height: "2px",
                          background: "linear-gradient(90deg, #E5243B, #DDA63A, #4C9F38)",
                          borderRadius: "1px",
                        }
                      : {},
                    "&:hover::after": {
                      content: '""', position: "absolute",
                      bottom: -2, left: 0, width: "100%", height: "2px",
                      background: "linear-gradient(90deg, #E5243B, #DDA63A, #4C9F38)",
                      borderRadius: "1px",
                    },
                  }}
                >
                  {link.label}
                </Typography>
              );
            })}
          </Stack>

          {/* ── Donate Button ── */}
          <Button
            component="a"
            href="/volunteer#ongoing-projects"
            variant="outlined"
            size="small"
            startIcon={<DonateIcon sx={{ fontSize: "14px !important" }} />}
            sx={{
              display: { xs: "none", md: "inline-flex" },
              px: { md: 1.8, lg: 2.2 },
              py: 0.9,
              mr: 1.5,
              borderColor: BROWN,
              color: BROWN,
              fontWeight: 700,
              fontSize: { md: "12px", lg: "13px" },
              letterSpacing: "0.3px",
              whiteSpace: "nowrap",
              borderRadius: "6px",
              textDecoration: "none",
              "&:hover": {
                bgcolor: BROWN,
                borderColor: BROWN,
                color: "#fff",
                boxShadow: `0 4px 16px rgba(92,51,17,0.25)`,
              },
              transition: "all 0.25s ease",
            }}
          >
            Donate
          </Button>

          {/* ── Join Us Button ── */}
          <Button
            variant="contained"
            component={Link}
            to="/volunteer"
            size="small"
            endIcon={<ArrowIcon />}
            sx={{
              display: { xs: "none", md: "inline-flex" },
              px: { md: 2, lg: 2.5 },
              py: 0.9,
              bgcolor: BROWN,
              color: "#fff",
              fontWeight: 700,
              fontSize: { md: "12px", lg: "13px" },
              letterSpacing: "0.3px",
              whiteSpace: "nowrap",
              borderRadius: "6px",
              "&:hover": { bgcolor: BROWN_DARK },
              boxShadow: "0 4px 16px rgba(92,51,17,0.30)",
            }}
          >
            Join Us
          </Button>

          {/* ── Mobile Hamburger ── */}
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{ display: { xs: "flex", md: "none" }, color: "#1a1a1a", ml: 1 }}
            aria-label="Open menu"
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* ── Mobile Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: { xs: "80vw", sm: 300 }, bgcolor: "#fff", px: 2, pt: 2 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography
            sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "15px", color: "#1a1a1a" }}
          >
            Build To Shelter Africa
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", height: "3px", mb: 2, borderRadius: 1, overflow: "hidden" }}>
          {SDG_COLORS.map((c, i) => <Box key={i} sx={{ flex: 1, bgcolor: c }} />)}
        </Box>

        <Divider sx={{ mb: 1 }} />

        <List disablePadding>
          {navLinks.map((link) => {
            const isActive    = pathname === link.path || pathname.startsWith(link.path + "/");
            const hasDropdown = !!link.dropdown;

            return (
              <Box key={link.path}>
                <ListItem
                  component={hasDropdown ? "div" : Link}
                  to={hasDropdown ? undefined : link.path}
                  onClick={() => {
                    if (hasDropdown) {
                      setMobileAboutOpen((p) => !p);
                    } else {
                      setDrawerOpen(false);
                    }
                  }}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    bgcolor: isActive ? "rgba(221,166,58,0.10)" : "transparent",
                    textDecoration: "none",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "rgba(221,166,58,0.08)" },
                  }}
                >
                  <ListItemText
                    primary={link.label}
                    primaryTypographyProps={{
                      fontSize: "15px",
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? GOLD : "#333",
                    }}
                  />
                  {hasDropdown && (
                    mobileAboutOpen
                      ? <ExpandLess sx={{ color: GOLD }} />
                      : <ExpandMore sx={{ color: "#999" }} />
                  )}
                </ListItem>

                {/* Mobile sub-items */}
                {hasDropdown && (
                  <Collapse in={mobileAboutOpen} timeout="auto" unmountOnExit>
                    <List disablePadding sx={{ pl: 2, mb: 0.5 }}>
                      {link.dropdown.map((sub) => (
                        <ListItem
                          key={sub.path}
                          component={Link}
                          to={sub.path}
                          onClick={() => setDrawerOpen(false)}
                          sx={{
                            borderRadius: 1,
                            mb: 0.5,
                            textDecoration: "none",
                            bgcolor: pathname === sub.path ? "rgba(221,166,58,0.10)" : "transparent",
                            "&:hover": { bgcolor: "rgba(221,166,58,0.08)" },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex", alignItems: "center", justifyContent: "center",
                              width: 28, height: 28, borderRadius: "7px",
                              bgcolor: "rgba(92,51,17,0.06)", color: BROWN, mr: 1.5,
                            }}
                          >
                            {sub.icon}
                          </Box>
                          <ListItemText
                            primary={sub.label}
                            primaryTypographyProps={{
                              fontSize: "14px",
                              fontWeight: pathname === sub.path ? 700 : 500,
                              color: pathname === sub.path ? GOLD : "#555",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </List>

        <Divider sx={{ my: 2 }} />

        <Button
          component="a"
          href="/volunteer#ongoing-projects"
          variant="outlined"
          fullWidth
          startIcon={<DonateIcon />}
          onClick={() => setDrawerOpen(false)}
          sx={{
            borderColor: BROWN, color: BROWN, fontWeight: 700,
            fontSize: "14px", py: 1.2, mb: 1.5, borderRadius: "6px",
            textDecoration: "none",
            "&:hover": { bgcolor: BROWN, color: "#fff", borderColor: BROWN },
          }}
        >
          Donate to a Project
        </Button>

        <Button
          variant="contained"
          component={Link}
          to="/volunteer"
          fullWidth
          endIcon={<ArrowIcon />}
          onClick={() => setDrawerOpen(false)}
          sx={{
            bgcolor: BROWN, color: "#fff", fontWeight: 700,
            fontSize: "14px", py: 1.2, borderRadius: "6px",
            "&:hover": { bgcolor: BROWN_DARK },
            boxShadow: "0 4px 16px rgba(92,51,17,0.25)",
          }}
        >
          Join Us
        </Button>
      </Drawer>
    </>
  );
}